import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { Avatar, Menu, MenuItem, Typography } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import Toolbar from '@mui/material/Toolbar';
import { FC, MouseEvent, useEffect, useState } from 'react';
import { NavLink, Link as ReactRouterLink, useNavigate } from 'react-router-dom';
import { images } from '../../../../shared/lib/assets';
import palette from '../../../../shared/lib/theme/palette';
import { DashboardHeaderStyle, listItemWrapper, mobileDrawerMenuItemStyle, mobileDrawerStyle } from '../../../../shared/lib/theme/styleConstants';
import { client } from '../../app/apollo';
import { AUTH_TOKEN, DASHBOARD_LINK, LINKS, PENDING_PROMO_CODE } from '../../app/constants';
import { withTrademarkTM } from '../../../../shared/lib/common/Tokenizer';
import { useAppContext } from '../../app/contexts/AppContext';
import { DashboardHeaderProps } from '../../app/interfaceTypes';
import { getToken } from '../../app/utils';

export const DashboardHeader: FC<DashboardHeaderProps> = ({ window }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { currentUser, setIsAppUserLoggedIn, setCurrentUser } = useAppContext();
  const navigate = useNavigate();
  const { blackOne, blackTwo } = palette;
  const { fullName, email } = currentUser ?? {};
  const fullNameValue = fullName?.split(' ');
  const firstInitial = fullNameValue?.[0].charAt(0);
  const lastInitial = fullNameValue?.[1].charAt(0);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const logout = async () => {
    localStorage.removeItem(AUTH_TOKEN);
    localStorage.removeItem(PENDING_PROMO_CODE);
    setIsAppUserLoggedIn(false);
    navigate(LINKS.LOGIN_LINK);
    setCurrentUser(null);
    handleClose();
    await client.clearStore();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const HandleDrawerClose = () => {
    setMobileOpen(false);
  };

  useEffect(() => {
    !getToken() && (async () => await logout())();
  }, [getToken()]);

  const drawer = (
    <Box sx={{ padding: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <ReactRouterLink to="/dashboard">
          <Box component="img" src={images.DASHBOARD_LOGO as unknown as string} display="block" />
        </ReactRouterLink>

        <IconButton onClick={HandleDrawerClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box>
        {DASHBOARD_LINK.map((item) => {
          const { link, title } = item;

          return (
            <ListItemButton component={NavLink} selected={location.pathname === link} to={link} key={link} sx={mobileDrawerMenuItemStyle}>
              {withTrademarkTM(title)}
            </ListItemButton>
          );
        })}
      </Box>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: 'flex', mb: 4 }}>
      <CssBaseline />

      <AppBar component="nav" sx={{ backgroundColor: 'white', boxShadow: '0px 8px 16px 0px rgba(145, 158, 171, 0.16)' }} position="sticky">
        <Toolbar disableGutters sx={{ padding: '16px', maxWidth: '1400px', width: '100%', margin: 'auto', justifyContent: 'space-between' }}>
          <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { sm: 'none' } }}>
            <MenuIcon sx={{ color: blackOne }} />
          </IconButton>

          <Box component="div" sx={{ display: { xs: 'none', sm: 'block', color: 'black' } }}>
            <ReactRouterLink to="/dashboard">
              <Box component="img" src={images.DASHBOARD_LOGO as unknown as string} display="block" />
            </ReactRouterLink>
          </Box>

          <Box display="flex">
            <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
              {DASHBOARD_LINK.map((item) => {
                const { link, title } = item;

                return (
                  <ListItemButton component={NavLink} selected={location.pathname === link} to={link} key={link} sx={DashboardHeaderStyle}>
                    {withTrademarkTM(title)}
                  </ListItemButton>
                );
              })}
            </Box>

            <IconButton id="basic-button" aria-controls={open ? 'basic-menu' : undefined} aria-haspopup="true" aria-expanded={open ? 'true' : undefined} onClick={handleClick}>
              <Avatar sx={{ mr: '10px' }}>
                <Typography variant="subtitle2" sx={{ color: blackTwo, fontWeight: '600' }}>
                  {firstInitial}
                  {lastInitial}
                </Typography>
              </Avatar>
            </IconButton>

            <Menu
              sx={listItemWrapper}
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button'
              }}
            >
              <MenuItem disableRipple sx={{ cursor: 'default', '&:hover': { backgroundColor: 'transparent' } }}>
                <Box>
                  <Typography variant="body2" sx={{ color: blackOne }}>
                    {fullName}
                  </Typography>

                  <Typography variant="subtitle2" sx={{ color: 'blackTwo', maxWidth: '180px' }} noWrap>
                    {email}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem onClick={() => navigate(LINKS.ACCOUNT_SETTINGS_LINK)}>Account Settings</MenuItem>
              <MenuItem onClick={() => { navigate(LINKS.CONTACT_US_LINK); handleClose(); }}>Contact Us</MenuItem>
              <MenuItem onClick={logout}>Logout</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ backgroundColor: 'white', boxShadow: '0px 8px 16px 0px rgba(145, 158, 171, 0.16)' }} component="nav">
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true
          }}
          sx={mobileDrawerStyle}
        >
          {drawer}
        </Drawer>
      </Box>
    </Box>
  );
};
